import Button from 'components/Button';
import { useSelectedStore, useSelectedStoreInfo } from 'contexts';
import { useFeatureToggle } from 'hooks';
import type { HTMLTagName } from 'types';
import { cnm } from 'utils/classNames';
import { getTestDataAttrFrom } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

interface Props {
	buttonClassName?: string;
	className?: string;
	container?: HTMLTagName;
	dataCy?: string;
	onClick: () => void;
}

/** Button for the global 'home store' selector in the header */
export default function GlobalStoreSelectorButton({
	buttonClassName,
	className,
	container: Container = 'div',
	dataCy,
	onClick,
}: Props) {
	const { t } = useI18n();
	const { selectedStore } = useSelectedStore();
	const { isLoading, storeInfo } = useSelectedStoreInfo();

	const { storesEnabled } = useFeatureToggle();
	if (!storesEnabled) {
		return null;
	}

	const buttonText = selectedStore ? (
		<>
			<span className="sr-only">{t('select_store_button_text')}, </span>
			<span className="font-bold">{selectedStore?.name}</span>
			{storeInfo?.todaysOpeningHours?.description && (
				<>: {storeInfo?.todaysOpeningHours?.description}</>
			)}
		</>
	) : (
		t('select_store_button_text')
	);

	return (
		<Container className={className}>
			<Button
				variant="text"
				onClick={onClick}
				aria-haspopup="dialog"
				size="sm"
				leadingIcon={isLoading ? 'loading' : 'store'}
				underline={false}
				className={cnm(
					'text-left align-middle hover:underline sm:text-md',
					buttonClassName,
				)}
				data-cy={getTestDataAttrFrom(dataCy)}
			>
				{buttonText}
			</Button>
		</Container>
	);
}
GlobalStoreSelectorButton.displayName = 'GlobalStoreSelectorButton';
